import { HTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export type BadgeVariant =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'success'
  | 'danger'
  | 'primary';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  children: ReactNode;
}

const variants: Record<BadgeVariant, ReturnType<typeof css>> = {
  neutral: css`
    background: ${({ theme }) => theme.colors.bgMuted};
    color: ${({ theme }) => theme.colors.textSecondary};
  `,
  info: css`
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(95, 177, 201, 0.18)'
        : 'rgba(4, 84, 113, 0.10)'};
    color: ${({ theme }) => theme.colors.info};
  `,
  warning: css`
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(240, 177, 74, 0.18)'
        : 'rgba(230, 154, 0, 0.12)'};
    color: ${({ theme }) => theme.colors.warning};
  `,
  success: css`
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(34, 182, 47, 0.18)'
        : 'rgba(7, 118, 17, 0.12)'};
    color: ${({ theme }) => theme.colors.success};
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.dangerBg};
    color: ${({ theme }) => theme.colors.danger};
  `,
  primary: css`
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(34, 182, 47, 0.18)'
        : 'rgba(7, 118, 17, 0.10)'};
    color: ${({ theme }) => theme.colors.primary};
  `,
};

interface StyledBadgeProps {
  $variant: BadgeVariant;
}

const Styled = styled.span<StyledBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  ${(props: StyledBadgeProps) => variants[props.$variant]}
`;

export const Badge = ({
  variant = 'neutral',
  children,
  ...rest
}: BadgeProps) => (
  <Styled $variant={variant} {...rest}>
    {children}
  </Styled>
);
